import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { UserContext } from "../../Context/usercontext";
import formatDate from "../../Utils/formateDate";
import {
  FaHeart,
  FaRegHeart,
  FaComment,
  FaShareAlt,
  FaEllipsisH,
  FaBookmark,
  FaRegBookmark,
  FaFlag,
  FaTimes
} from "react-icons/fa";
import { FiSend } from "react-icons/fi";
import {
  HiOutlineCalendar,
  HiOutlineLocationMarker,
  HiOutlineUserGroup,
  HiOutlineAcademicCap
} from "react-icons/hi";
import axios from "axios";
import Cookies from "js-cookie";
// import Header from "../Header/Header";
import UpcomingEvents from "./components/UpcomingEvents";
import AdmissionsOpen from "./components/AdmissionsOpen";
import PostSkeleton from "./components/PostSkeleton";

const PostById = () => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentInput, setCommentInput] = useState("");
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const { user } = useContext(UserContext);
  const { id: postId } = useParams();

  const userId = user?._id;

  const fetchPost = async () => {
    try {
      const token = Cookies.get("userToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/apis/admin/getSinglePost/${postId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const postData = response.data?.data;
      
      setPost(postData);
      setLikeCount(postData?.likeCount || 0);
    } catch (error) {
      console.error("Error fetching post:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPost();
  }, [postId]);

  useEffect(() => {
    if (post && userId) {
      setComments(post?.comments || []);
      setLiked(post?.likes?.some((like) => like?._id === userId));
    }
  }, [post, userId]);

  const handleLikeClick = async () => {
    try {
      const token = Cookies.get("userToken");
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/apis/admin/toggleLikePost/${postId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setLiked(response.data.data.liked);
      setLikeCount(prev => response.data.data.liked ? prev + 1 : prev - 1);
    } catch (error) {
      console.error("Error toggling like:", error);
    }
  };

  const handleCommentSubmit = async () => {
    if (!commentInput.trim()) return;
    try {
      const token = Cookies.get("userToken");
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/apis/admin/addComment/${postId}`,
        { text: commentInput },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const newComment = response.data?.data?.comment;
      newComment.user = {
        _id: user._id,
        fullName: user.fullName,
      };
      setComments((prev) => [...prev, newComment]);
      setCommentInput("");
      setShowCommentInput(false);
    } catch (error) {
      console.error("Error submitting comment:", error);
    }
  };

  const handleShareClick = () => {
    const shareData = {
      title: post?.adminProfile?.collegeName || "College Post",
      text: post?.caption,
      url: `${window.location.origin}/posts/${postId}`,
    };

    try {
      if (navigator.share) {
        navigator.share(shareData);
      } else {
        navigator.clipboard.writeText(shareData.url);
        alert("Link copied to clipboard!");
      }
    } catch (err) {
      console.error("Error sharing:", err);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  const commentVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: { delay: i * 0.1 }
    })
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        {/* <Header /> */}
        <div className="container mx-auto px-4 py-6">
          <PostSkeleton />
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        {/* <Header /> */}
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-12 max-w-md mx-auto border border-indigo-100">
            <div className="w-20 h-20 mx-auto bg-indigo-100 rounded-full flex items-center justify-center mb-4">
              <HiOutlineAcademicCap className="w-10 h-10 text-indigo-600" />
            </div>
            <p className="text-gray-600 text-lg">Post not found</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex flex-col"
    >
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-r from-blue-200 to-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-70"
          animate={{
            x: [0, 50, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-r from-purple-200 to-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-70"
          animate={{
            x: [0, -50, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
      </div>

      {/* <Header /> */}

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="container mx-auto px-4 py-6 flex-1 flex flex-col lg:flex-row gap-6"
      >
        {/* Left Sidebar - Upcoming Events */}
        <motion.div variants={itemVariants} className="w-full lg:w-3/12 lg:sticky lg:top-6">
          <UpcomingEvents />
        </motion.div>

        {/* Main Content - Post */}
        <motion.div variants={itemVariants} className="w-full lg:w-6/12">
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border border-indigo-100">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-indigo-100">
              <div className="flex items-center space-x-3">
                <img
                  src={
                    post.adminProfile?.image ||
                    "https://static.vecteezy.com/system/resources/thumbnails/036/594/092/small/man-empty-avatar-photo-placeholder-for-social-networks-resumes-forums-and-dating-sites-male-and-female-no-photo-images-for-unfilled-user-profile-free-vector.jpg"
                  }
                  alt="College Logo"
                  className="w-12 h-12 rounded-full object-cover border-2 border-indigo-200"
                />
                <div>
                  <h3 className="font-bold text-gray-800">
                    {post.adminProfile?.collegeName || "College Admin"}
                  </h3>
                  <p className="text-xs text-indigo-600">{formatDate(post.createdAt)}</p>
                </div>
              </div>

              {/* Menu Button */}
              <div className="relative">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-full transition"
                >
                  <FaEllipsisH />
                </motion.button>

                <AnimatePresence>
                  {menuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl z-10 border border-indigo-100 overflow-hidden"
                    >
                      <div className="py-1">
                        <button
                          onClick={() => {
                            setSaved(!saved);
                            setMenuOpen(false);
                          }}
                          className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-indigo-50 w-full text-left transition"
                        >
                          {saved ? (
                            <FaBookmark className="mr-3 text-indigo-600" />
                          ) : (
                            <FaRegBookmark className="mr-3" />
                          )}
                          {saved ? "Saved" : "Save Post"}
                        </button>
                        <button
                          onClick={() => {
                            handleShareClick();
                            setMenuOpen(false);
                          }}
                          className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-indigo-50 w-full text-left transition"
                        >
                          <FaShareAlt className="mr-3" />
                          Share
                        </button>
                        <button
                          onClick={() => {
                            alert("Post reported to moderators");
                            setMenuOpen(false);
                          }}
                          className="flex items-center px-4 py-3 text-sm text-red-600 hover:bg-red-50 w-full text-left transition"
                        >
                          <FaFlag className="mr-3" />
                          Report
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Content */}
            <div className="p-5">
              <p className="text-gray-800 mb-4 leading-relaxed">{post.caption}</p>

              {post.image && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="relative pt-[56.25%] rounded-xl overflow-hidden bg-gradient-to-br from-indigo-100 to-purple-100 border border-indigo-100"
                >
                  {/\.(mp4|webm|ogg|wmv)$/i.test(post.image) ? (
                    <video controls className="absolute top-0 left-0 w-full h-full object-cover">
                      <source src={post.image} />
                      Your browser does not support video playback.
                    </video>
                  ) : (
                    <img
                      src={post.image}
                      alt="Post content"
                      className="absolute top-0 left-0 w-full h-full object-cover"
                    />
                  )}
                </motion.div>
              )}
            </div>

            {/* Actions */}
            <div className="px-5 py-3 border-t border-indigo-100">
              <div className="flex justify-between items-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleLikeClick}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition ${liked ? "text-red-500 bg-red-50" : "text-gray-500 hover:bg-red-50"
                    }`}
                >
                  {liked ? <FaHeart className="animate-pulse" /> : <FaRegHeart />}
                  <span className="font-medium">{likeCount} Likes</span>
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowCommentInput(!showCommentInput)}
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg text-gray-500 hover:bg-indigo-50 transition"
                >
                  <FaComment />
                  <span className="font-medium">Comment</span>
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleShareClick}
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg text-gray-500 hover:bg-indigo-50 transition"
                >
                  <FaShareAlt />
                  <span className="font-medium">Share</span>
                </motion.button>
              </div>

              {/* Comment Input */}
              <AnimatePresence>
                {showCommentInput && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-4 pt-4 border-t border-indigo-100"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center justify-center text-white font-bold">
                        {user?.fullName?.[0] || "U"}
                      </div>
                      <div className="flex-1 relative">
                        <input
                          type="text"
                          value={commentInput}
                          onChange={(e) => setCommentInput(e.target.value)}
                          placeholder="Write a comment..."
                          className="w-full py-3 px-4 bg-indigo-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-300 border border-indigo-100"
                          onKeyPress={(e) => e.key === 'Enter' && handleCommentSubmit()}
                        />
                        {commentInput && (
                          <motion.button
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            onClick={handleCommentSubmit}
                            className="absolute right-3 top-3 text-indigo-600 hover:text-indigo-700"
                          >
                            <FiSend className="w-5 h-5" />
                          </motion.button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Comments List */}
              {comments.length > 0 && (
                <div className="mt-4 border-t border-indigo-100 pt-4 max-h-60 overflow-y-auto space-y-3 custom-scrollbar">
                  <AnimatePresence>
                    {comments.map((comment, index) => (
                      <motion.div
                        key={comment._id}
                        custom={index}
                        variants={commentVariants}
                        initial="hidden"
                        animate="visible"
                        className="flex items-start space-x-3"
                      >
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-indigo-100 to-purple-100 flex items-center justify-center text-indigo-600 font-bold text-sm">
                          {comment.user?.fullName?.[0] || "U"}
                        </div>
                        <div className="flex-1 bg-indigo-50/50 rounded-xl p-3">
                          <p className="text-sm">
                            <span className="font-semibold text-gray-800 mr-2">
                              {comment.user?.fullName || "User"}:
                            </span>
                            <span className="text-gray-600">{comment.text}</span>
                          </p>
                          <p className="text-xs text-indigo-400 mt-1">
                            {formatDate(comment.createdAt)}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Right Sidebar - Admissions Open */}
        <motion.div variants={itemVariants} className="w-full lg:w-3/12 lg:sticky lg:top-6">
          <AdmissionsOpen />
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default PostById;